import { useState, useEffect } from "react";
import { GripVertical, Eye, EyeOff } from "lucide-react";
import { DndContext, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, closestCorners, useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "@/lib/api";

const MAIN_DEF = ["quicktiles", "allowance", "tasks"];
const SIDE_DEF = ["mededelingen", "nieuws", "poll", "cook_tonight", "cook_proposal", "vieringen", "pindates", "boodschappen", "nu_log", "swaps", "modules", "qr"];
const ALL = [...MAIN_DEF, ...SIDE_DEF];
export const KIOSK_BLOCK_LABELS = {
  quicktiles: "⚡ Quick Tiles", allowance: "💰 Zakgeld-widget", tasks: "✅ Taken",
  mededelingen: "📣 Mededelingen", nieuws: "📰 Nieuws", poll: "🍽️ Poll / Eten",
  cook_tonight: "🍳 Kookt vanavond", cook_proposal: "🍳 Kookplanning", vieringen: "🎉 Vieringen",
  pindates: "📌 Pin Dates", boodschappen: "🛒 Boodschappen", nu_log: "🚨 NU!-logboek", swaps: "🔄 Taakruilingen", modules: "🧩 Meer-tegels", qr: "📱 Op je telefoon (QR)",
};

const resolveCols = (layout) => {
  layout = layout || {};
  const sm = Array.isArray(layout.main) ? layout.main.filter((id) => ALL.includes(id)) : null;
  const ss = Array.isArray(layout.side) ? layout.side.filter((id) => ALL.includes(id)) : null;
  if (sm === null && ss === null) return { main: [...MAIN_DEF], side: [...SIDE_DEF] };
  const used = new Set([...(sm || []), ...(ss || [])]);
  return {
    main: [...(sm || []), ...MAIN_DEF.filter((id) => !used.has(id))],
    side: [...(ss || []), ...SIDE_DEF.filter((id) => !used.has(id))],
  };
};

function Row({ id, hidden, onToggleHide }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`kiosk-layout-item-${id}`}
      className={`brutal-sm rounded-xl bg-white px-2.5 py-2.5 flex items-center gap-2 font-bold text-sm ${hidden ? "opacity-40" : ""}`}>
      <button {...attributes} {...listeners} data-testid={`kiosk-layout-grip-${id}`}
        className="cursor-grab active:cursor-grabbing touch-none shrink-0 text-brand-ink/40" aria-label="Sleep">
        <GripVertical className="w-4 h-4" strokeWidth={2.5} />
      </button>
      <span className="truncate flex-1 select-none">{KIOSK_BLOCK_LABELS[id] || id}</span>
      <button onClick={onToggleHide} data-testid={`kiosk-layout-hide-${id}`}
        className="shrink-0 brutal-press rounded-full p-1 text-brand-ink/50"
        aria-label={hidden ? "Tonen" : "Verbergen"} title={hidden ? "Tonen op kiosk" : "Verbergen op kiosk"}>
        {hidden ? <EyeOff className="w-4 h-4" strokeWidth={2.5} /> : <Eye className="w-4 h-4" strokeWidth={2.5} />}
      </button>
    </div>
  );
}

function Column({ col, title, items, hidden, onToggleHide }) {
  const { setNodeRef, isOver } = useDroppable({ id: col });
  return (
    <div>
      <p className="font-bold text-sm mb-1.5">{title}</p>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div ref={setNodeRef} data-testid={`kiosk-layout-${col}`}
          className={`space-y-1.5 min-h-[60px] rounded-2xl p-1.5 transition-colors ${isOver ? "bg-brand-violet/10" : "bg-brand-ink/5"}`}>
          {items.length === 0 && <p className="text-xs text-brand-ink/40 font-semibold px-2 py-3 text-center">Sleep hier een blok naartoe</p>}
          {items.map((id) => <Row key={id} id={id} hidden={hidden.has(id)} onToggleHide={() => onToggleHide(id)} />)}
        </div>
      </SortableContext>
    </div>
  );
}

const _annActive = (a) => { const d = new Date().toISOString().slice(0, 10); return a.pinned && (!a.pinned_until || a.pinned_until >= d); };
const _modOn = (board, k) => board?.modules_on?.[k] !== false;

// Bepaalt of een blok inhoud heeft (mirrort de kioskweergave) + de echte regels voor de preview
function blockContent(id, board) {
  if (!board) return { show: true, lines: [] };
  switch (id) {
    case "quicktiles": { const q = board.quick_tiles || []; return q.length ? { show: true, lines: q.slice(0, 6).map((t) => t.title) } : { show: false }; }
    case "allowance": { const a = board.allowance; return (board.kiosk_show_allowance !== false && a && a.members?.length) ? { show: true, title: `${a.title} — deze week`, lines: a.members.slice(0, 4).map((m) => `${m.name}${m.earned ? " ✓" : ` · nog ${m.needed}`}`) } : { show: false }; }
    case "tasks": { const ts = board.open_tasks || []; return { show: true, title: "Openstaande taken", lines: ts.length ? ts.slice(0, 4).map((t) => t.title) : ["Niks te doen 🎉"] }; }
    case "mededelingen": { const r = (board.announcements || []).filter((a) => !_annActive(a)); return (_modOn(board, "mededelingen") && r.length) ? { show: true, lines: r.slice(0, 4).map((a) => a.title) } : { show: false }; }
    case "nieuws": { const s = board.stories || []; return (_modOn(board, "nieuws") && s.length) ? { show: true, lines: s.slice(0, 4).map((x) => x.title) } : { show: false }; }
    case "poll": { const p = board.latest_poll; return (_modOn(board, "polls") && p) ? { show: true, title: p.question, lines: (p.options || []).slice(0, 4).map((o) => { const c = Object.values(p.votes || {}).filter((v) => v === o).length; return `${o} · ${c}`; }) } : { show: false }; }
    case "cook_tonight": { const c = board.cook_tonight || []; return (_modOn(board, "polls") && c.length) ? { show: true, lines: c.map((x) => `🍳 ${(x.assignees || []).map((a) => a.name).join(" & ")}`) } : { show: false }; }
    case "cook_proposal": { const c = board.cook_proposal; return (_modOn(board, "polls") && c) ? { show: true, title: "Plan het koken", lines: [`Winnaar: ${c.winner}`] } : { show: false }; }
    case "vieringen": { const c = board.celebrations || []; return (_modOn(board, "vieringen") && c.length) ? { show: true, lines: c.slice(0, 4).map((x) => x.title) } : { show: false }; }
    case "pindates": { const p = board.pindates || []; return (_modOn(board, "pindates") && p.length) ? { show: true, lines: p.slice(0, 4).map((x) => x.title) } : { show: false }; }
    case "boodschappen": { const s = board.shopping || []; return (board.shopping_enabled && s.length) ? { show: true, lines: s.slice(0, 4).map((x) => x.name) } : { show: false }; }
    case "nu_log": { const n = board.recent_nudges || []; return n.some((x) => !x.done) ? { show: true, lines: n.slice(0, 4).map((x) => `${x.task_title}${x.done ? " ✅" : ""}`) } : { show: false }; }
    case "swaps": { const s = board.open_swaps || []; return (_modOn(board, "taakruilingen") && s.length) ? { show: true, lines: s.slice(0, 4).map((x) => x.task_title) } : { show: false }; }
    case "modules": { const m = board.kiosk_more_modules; const n = Array.isArray(m) ? m.length : null; return { show: true, title: "Meer", lines: n != null ? [`${n} tegels`] : [] }; }
    case "qr": return (board.kiosk_show_phone_tile !== false) ? { show: true, title: "Op je telefoon", lines: ["Scan de QR-code"] } : { show: false };
    default: return { show: false };
  }
}

function PBlock({ id, board }) {
  const c = blockContent(id, board);
  if (!c.show) return null;
  const parts = (KIOSK_BLOCK_LABELS[id] || id).split(" ");
  const emoji = parts[0];
  const name = parts.slice(1).join(" ");
  return (
    <div data-testid={`kiosk-preview-${id}`} className="rounded-lg bg-white/95 text-brand-ink brutal-sm px-1.5 py-1 overflow-hidden">
      <p className="text-[10px] font-black leading-tight truncate flex items-center gap-1"><span>{emoji}</span><span className="truncate">{c.title || name}</span></p>
      {(c.lines || []).slice(0, 4).map((l, i) => (<p key={i} className="text-[9px] leading-tight truncate text-brand-ink/60">• {l}</p>))}
    </div>
  );
}

function MiniPreview({ cols, hidden, board }) {
  const render = (id, col) => {
    if (!board) return (
      <div key={id} data-testid={`kiosk-preview-${col}-${id}`} className="rounded-lg bg-white/95 text-brand-ink text-[10px] font-bold px-1.5 py-1 truncate leading-tight brutal-sm">
        {KIOSK_BLOCK_LABELS[id] || id}
      </div>
    );
    return <PBlock key={id} id={id} board={board} />;
  };
  const mainVis = cols.main.filter((id) => !hidden.has(id));
  const sideVis = cols.side.filter((id) => !hidden.has(id));
  const hiddenList = [...hidden];
  return (
    <div data-testid="kiosk-layout-preview-wrap">
      <p className="font-bold text-sm mb-1.5">Live voorbeeld {!board && <span className="text-brand-ink/40 font-semibold">(laden…)</span>}</p>
      <div className="rounded-2xl p-2.5 navy-gradient" data-testid="kiosk-layout-preview">
        <div className="rounded-md bg-white/15 h-4 mb-2 flex items-center px-2"><span className="text-white/70 text-[9px] font-black tracking-wide">SLACKR — KIOSK</span></div>
        <div className="grid grid-cols-3 gap-1.5 items-start">
          <div className="col-span-2 space-y-1.5">
            {mainVis.map((id) => render(id, "main"))}
          </div>
          <div className="col-span-1 space-y-1.5">
            {sideVis.map((id) => render(id, "side"))}
          </div>
        </div>
      </div>
      {hiddenList.length > 0 && (
        <p className="text-[11px] text-brand-ink/50 mt-1.5" data-testid="kiosk-layout-preview-hidden">
          Verborgen: {hiddenList.map((id) => KIOSK_BLOCK_LABELS[id] || id).join(", ")}
        </p>
      )}
    </div>
  );
}

export default function KioskLayoutEditor({ initial, onSave, t = (s) => s }) {
  const [cols, setCols] = useState(() => resolveCols(initial));
  const [hidden, setHidden] = useState(() => new Set(Array.isArray(initial?.hidden) ? initial.hidden : []));
  const [board, setBoard] = useState(null);
  useEffect(() => { api.get("/kiosk/board").then((r) => setBoard(r.data)).catch(() => {}); }, []);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const findContainer = (id, state = cols) => {
    if (id === "main" || id === "side") return id;
    if (state.main.includes(id)) return "main";
    if (state.side.includes(id)) return "side";
    return null;
  };

  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const from = findContainer(active.id);
    const to = findContainer(over.id);
    if (!from || !to || from === to) return;
    setCols((prev) => {
      const src = [...prev[from]];
      const dst = [...prev[to]];
      const i = src.indexOf(active.id);
      if (i < 0) return prev;
      src.splice(i, 1);
      const overIdx = dst.indexOf(over.id);
      dst.splice(overIdx >= 0 ? overIdx : dst.length, 0, active.id);
      return { ...prev, [from]: src, [to]: dst };
    });
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    const from = findContainer(active.id);
    const to = findContainer(over.id);
    let next = cols;
    if (from && to && from === to && active.id !== over.id) {
      const arr = cols[from];
      const oldI = arr.indexOf(active.id);
      const newI = arr.indexOf(over.id);
      if (oldI >= 0 && newI >= 0) next = { ...cols, [from]: arrayMove(arr, oldI, newI) };
      setCols(next);
    }
    onSave({ main: next.main, side: next.side, hidden: [...hidden] });
  };

  const toggleHide = (id) => {
    const n = new Set(hidden);
    n.has(id) ? n.delete(id) : n.add(id);
    setHidden(n);
    onSave({ main: cols.main, side: cols.side, hidden: [...n] });
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-2 gap-3">
          <Column col="main" title={t("Hoofdkolom")} items={cols.main} hidden={hidden} onToggleHide={toggleHide} />
          <Column col="side" title={t("Zijkolom")} items={cols.side} hidden={hidden} onToggleHide={toggleHide} />
        </div>
      </DndContext>
      <MiniPreview cols={cols} hidden={hidden} board={board} />
    </div>
  );
}
